import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { OnboardingService } from '../../generated/auth/services/onboarding.service';
import { Permission } from '../../models/permission.model';
import { selectUserInformation } from '../auth/auth.selectors';
import * as OnboardingActions from './advisor-onboarding.actions';
import { defaultOnboardingFilters, OnboardingLoadParams } from './advisor-onboarding.model';
import { IOnboardingState } from './advisor-onboarding.state';

@Injectable()
export class AdvisorOnboardingEffects {
  private actions$ = inject(Actions);
  private onboardingService = inject(OnboardingService);
  private store = inject(Store<{ 'ADVISOR-ONBOARDING': IOnboardingState }>);

  loadOnboardings$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OnboardingActions.loadOnboardings),
      withLatestFrom(
        this.store.select((state) => state['ADVISOR-ONBOARDING']),
        this.store.select(selectUserInformation),
      ),
      switchMap(([action, state, userInformation]) => {
        const filters = state?.filters || defaultOnboardingFilters;
        const params: OnboardingLoadParams = {
          ...filters,
          Page: action.params?.Page || filters.Page,
          PageSize: action.params?.PageSize || filters.PageSize,
        };

        // Clean undefined values from params
        const cleanParams: any = {};
        Object.keys(params).forEach((key) => {
          if (params[key as keyof OnboardingLoadParams] !== undefined) {
            cleanParams[key] = params[key as keyof OnboardingLoadParams];
          }
        });

        const roles = userInformation?.roles ?? [];
        const isReviewer = roles.includes(Permission.ONBOARDING_FRONT_OFFICE);
        const apiCall = isReviewer
          ? this.onboardingService.onboardingAdvisorOnboardingGetOnboardingsForReviewGet(cleanParams)
          : this.onboardingService.onboardingAdvisorOnboardingGetCreatorOnboardingsGet(cleanParams);

        return apiCall.pipe(
          map((response) =>
            OnboardingActions.loadOnboardingsSuccess({
              onboardings: response.result?.items || [],
              totalCount: response.result?.meta?.count || 0,
              currentPage: cleanParams.Page || 1,
            }),
          ),
          catchError((error) =>
            of(OnboardingActions.loadOnboardingsFailure({ error: error.message })),
          ),
        );
      }),
    ),
  );

  reloadOnFiltersChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        OnboardingActions.setFilter,
        OnboardingActions.resetFilter,
        OnboardingActions.updatePagination,
      ),
      map(() => OnboardingActions.loadOnboardings({})),
    ),
  );
}
